<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import ErrorBanner from './ErrorBanner.vue'

const { t } = useI18n()
const session = useSession()
const route = useRoute()
const router = useRouter()
const menuOpen = ref(false)
const sw = useForm()

watch(() => route.fullPath, () => (menuOpen.value = false))

// Nav is driven by workspace kind: business-only pages exist only in business workspaces.
const nav = computed(() => [
  { to: '/', label: t('nav.home') },
  { to: '/transactions', label: t('nav.transactions') },
  { to: '/accounts', label: t('nav.accounts') },
  { to: '/import', label: t('nav.import') },
  { to: '/rules', label: t('nav.rules') },
  { to: '/bills', label: t('nav.bills') },
  { to: '/documents', label: t('nav.documents') },
  { to: '/utilities', label: t('nav.utilities') },
  { to: '/reconciliation', label: t('nav.reconciliation') },
  { to: '/budget', label: t('nav.budget') },
  { to: '/savings', label: t('nav.savings') },
  { to: '/obligations', label: t('nav.obligations') },
  ...(session.isBusiness ? [{ to: '/company', label: t('nav.company') }] : []),
  { to: '/members', label: t('nav.members') },
  { to: '/workspaces', label: t('nav.workspaces') },
  { to: '/settings', label: t('nav.settings') },
])

async function onSwitch(e: Event) {
  const select = e.target as HTMLSelectElement
  const ok = await sw.submit(() => session.switchWorkspace(select.value))
  if (!ok) select.value = session.workspace?.id ?? ''
  else if (route.meta.business && !session.isBusiness) router.push('/')
}

async function logout() {
  await session.logout()
  router.push('/login')
}
</script>

<template>
  <div class="shell">
    <header class="mobile-bar">
      <span class="brand">Creditify</span>
      <button class="menu-btn" :aria-expanded="menuOpen" aria-controls="sidebar" @click="menuOpen = !menuOpen">
        <span class="sr-only">{{ t('nav.menu') }}</span>
        <span aria-hidden="true">☰</span>
      </button>
    </header>

    <aside id="sidebar" :class="['sidebar', { open: menuOpen }]">
      <div class="brand desktop-only">Creditify</div>

      <label class="switcher">
        <span class="muted small">{{ t('nav.workspace') }}</span>
        <select data-testid="workspace-switcher" :value="session.workspace?.id" :disabled="sw.busy.value" @change="onSwitch">
          <option v-for="w in session.workspaces" :key="w.id" :value="w.id" :disabled="w.suspended"
            :title="`${w.name} · ${t(`workspace.kind.${w.kind}`)}`">
            {{ w.kind === 'business' ? `${w.name} · ${t('workspace.kind.business')}` : w.name }}
          </option>
        </select>
      </label>
      <ErrorBanner :error="sw.error.value" />

      <nav class="nav">
        <RouterLink v-for="n in nav" :key="n.to" :to="n.to" class="nav-link" exact-active-class="active">{{ n.label }}</RouterLink>
      </nav>

      <div class="spacer" />
      <div class="user">
        <div class="user-name">{{ session.user?.display_name }}</div>
        <div class="muted small user-email">{{ session.user?.email }}</div>
        <button class="link-btn" data-testid="logout" @click="logout">{{ t('nav.logout') }}</button>
      </div>
    </aside>
    <div v-if="menuOpen" class="scrim" @click="menuOpen = false" />

    <main class="content">
      <div v-if="session.workspace?.suspended" class="suspended" role="status">{{ t('workspace.suspendedBanner') }}</div>
      <slot />
    </main>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: 248px 1fr;
  min-height: 100vh;
}
.sidebar {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1rem;
  border-right: 1px solid var(--border);
  background: var(--bg-card);
  overflow-y: auto;
}
.brand {
  font-weight: 700;
  font-size: 1.1rem;
}
.switcher {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-link {
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-sm);
  color: var(--fg);
}
.nav-link:hover {
  background: var(--bg);
  text-decoration: none;
}
.nav-link.active {
  background: rgb(var(--accent-rgb) / 0.1);
  color: var(--accent);
  font-weight: 500;
}
.user {
  border-top: 1px solid var(--border);
  padding-top: 0.75rem;
}
.user-name {
  font-weight: 500;
}
.user-email {
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-btn {
  margin-top: 0.4rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  cursor: pointer;
}
.content {
  padding: 2rem;
  min-width: 0;
}
.suspended {
  margin-bottom: 1.5rem;
  padding: 0.65rem 0.9rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--warn);
  color: var(--warn);
}
.mobile-bar,
.scrim {
  display: none;
}

@media (max-width: 860px) {
  .shell {
    grid-template-columns: 1fr;
  }
  .mobile-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 20;
    padding: 0.75rem 16px;
    background: var(--bg-card);
    border-bottom: 1px solid var(--border);
  }
  .menu-btn {
    font-size: 1.3rem;
    background: none;
    border: 0;
    color: var(--fg);
    cursor: pointer;
  }
  .desktop-only {
    display: none;
  }
  .sidebar {
    position: fixed;
    z-index: 40;
    left: 0;
    top: 0;
    width: min(280px, 85vw);
    transform: translateX(-100%);
    transition: transform 0.2s;
  }
  .sidebar.open {
    transform: none;
  }
  .scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 30;
    background: rgb(0 0 0 / 0.3);
  }
  .content {
    padding: 1.25rem 16px;
  }
}
</style>
